<template>
  <div
    class="attach-file"
    :class="disabled ? 'attach-file--disabled' : null"
    @dragenter.prevent.stop="onDragStart"
    @dragover.prevent.stop="onDragStart"
    @dragleave.prevent.stop="onDragStop"
    @drop.prevent.stop="onDrop"
  >
    <input type="file" :id="id" class="attach-file__input" @change="({ target: { files = [] } }) => checkFileTypes(files[0])" :disabled="disabled">
    <div class="d-flex justify-center attach-file__container" :class="isOver ? 'attach-file__container--over' : ''" v-if="!computedFile">
      <div class="attach-file__button-container" style="flex-shrink: 0;">
        <label class="attach-file__button" :for="id">
          <span class="attach-file__button-inner">
            <span class="attach-file-content d-flex align-center justify-center">
              <span class="d-block icon-upload mr-05 x text--xs-small" style="padding-top: .15rem;"></span>
              <span v-if="uploading && percent < 100" class="d-block">{{ $t('controls.UploadSingle.uploadingWithPercent', {percent}) }}</span>
              <span v-else-if="uploading" class="d-block"> {{ $t('controls.UploadSingle.uploading') }} </span>
              <span v-else class="d-block">{{ $t('controls.UploadSingle.uploadFile') }}</span>
              <spinner class="attach-file__button-spinner d-block ml-05" width="2" v-if="uploading && percent === 100"></spinner>
            </span>
          </span>
        </label>
      </div>
      <div class="attach-file__arrow text-color-grey text--small" v-if="computedDescription && !disabled">←</div>
      <div class="attach-file__description text-color-grey text--small" v-if="computedDescription && !disabled">{{computedDescription}}</div>
    </div>

    <div class="attach-file__container d-flex align-center justify-center" v-else>
      <div class="attach-file__uploaded d-flex align-center">
        <a :href="computedFile.downloadUrl ? computedFile.downloadUrl : `${$getEnv.staticPath}/${computedFile.path}`" class="attach-file__link d-block" target="_blank"><i class="attach-file__attachment-icon icon-attachment mr-05"></i>{{computedFile.origin}}</a>
        <a href="#" @click.prevent="unAttach(computedFile.name)" class="attach-file__delete ml-2 d-block" v-if="!disabled"><i class="attach-file__delete-icon icon-close"></i></a>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'

import Spinner from '@/ui/elements/Spinner'

import { SingleUploadGQL, SingleUploadDeleteGQL } from '@/graphql/mutations'

export default {
  name: 'upload-single',

  components: {
    Spinner
  },

  data () {
    return {
      id: uuidv4(),

      isOver: false,

      fileDataLocal: null,

      uploading: false,
      percent: 0
    }
  },

  props: {
    fileExtensions: {
      type: Array,
      default: function () {
        return []
      }
    },

    value: {
      type: Object,
      default: null
    },

    description: {
      type: String,
      default: null
    },

    buttonText: {
      type: String,
      default: null
    },

    clientOnly: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    computedDescription () {
      if (!this.description) {
        return this.$t('components.UploadSingle.description.default')
      }

      return this.description
    },

    computedButtonText () {
      if (!this.buttonText) {
        return this.$t('components.UploadSingle.buttonText.default')
      }

      return this.buttonText
    },

    computedFile: {
      get () {
        if (!this.value) {
          return this.fileDataLocal
        }

        return this.value
      },

      set (fileData) {
        this.$emit('input', fileData)
        this.fileDataLocal = fileData
      }
    }
  },

  methods: {
    onDragStart () {
      if (this.disabled) {
        return
      }
      this.isOver = true
    },

    onDragStop () {
      if (this.disabled) {
        return
      }
      this.isOver = false
    },

    onDrop (e) {
      if (this.disabled) {
        return
      }
      this.isOver = false

      const dt = e.dataTransfer
      const files = [...dt.files]

      if (this.fileExtensions.length) {
        this.checkFileTypes(files[0])
      } else {
        this.upload(files[0])
      }
    },

    checkFileTypes (file) {
      const isExtensionCorrect = this.fileExtensions.some(type => {
        const regexp = new RegExp(`[.]${type}$`)
        return !!file.name.match(regexp)
      })
      if (isExtensionCorrect) {
        this.upload(file)
      } else {
        this.$notify({
          type: 'error',
          message: this.$i18n.t('components.Profile.formFields.error.fileExtantionError'),
          timeout: 3000
        })
      }
    },

    async upload (file) {
      if (!file) {
        return
      }

      this.uploading = true
      this.percent = 0
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SingleUploadGQL,
          variables: {
            file
          },
          context: {
            fetchOptions: {
              customFetch: true,
              onProgress: (ev) => {
                this.percent = Math.floor(ev.loaded / ev.total * 100)
              },
              onAbortPossible: () => {}
            }
          }
        })

        this.computedFile = data.singleUpload
        this.uploading = false
      } catch (e) {
        this.uploading = false
        throw new Error(e)
      }
    },

    async unAttach (fileName) {
      if (!fileName) {
        return
      }

      if (!this.clientOnly) {
        await this.$apollo.mutate({
          mutation: SingleUploadDeleteGQL,
          variables: {
            fileName
          }
        })
      }

      document.getElementById(this.id).value = ''
      this.computedFile = null
    }
  }
}
</script>

<style lang="scss">
  .attach-file {
    &__input {
      display: none;
    }

    &__container {
      padding: 1rem;
      transition: all .2s;

      width: 100%;
      @include respond(small-desktop) {
        width: unset;
      }

      background-color: $color-grey-extra-light;
      border: 2px dashed darken($color-grey-extra-light, 5);
      border-radius: $border-radius;

      &--over {
        border-color: $color-purple-light;
      }
    }

    &__button {
      display: inline-block;
      background: linear-gradient(180deg, rgba($color-space-light, 0) 4.17%, rgba($color-space-light, 0.530201) 67.19%, $color-space-light 100%);
      border: 1px solid $color-space-lighten;
      border-radius: $border-radius;
      padding: .8rem .9rem .7rem .9rem;
      cursor: pointer;
      transition: all .2s;
      width: 100%;

      @include respond(small-desktop) {
        width: unset;
      }

      &-spinner {
        margin-top: -.15rem;
        margin-bottom: -.1rem;
      }

      &:hover {
        background: linear-gradient(180deg, rgba(darken($color-space-light, 5), 0) 4.17%, darken(lighten($color-space-light, 5), 0.530201) 67.19%, darken($color-space-light, 5) 100%);
      }
    }

    &__button-container {
      width: 100%;
      @include respond(small-desktop) {
        width: unset;
      }
    }

    &__uploaded {
      padding: .8rem .9rem .8rem .9rem;
    }

    &__arrow {
      padding-top: .9rem;
      margin-left: .7rem;
      display: none;

      @include respond(small-desktop) {
        display: unset;
      }
    }

    &__description {
      margin-left: .2rem;
      padding-top: .9rem;
      max-width: 18rem;
      display: none;

      @include respond(small-desktop) {
        display: block;
      }
    }

    &__delete {
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 50%;
      background-color: $color-error;
      flex-shrink: 0;

      position: relative;
    }

    &__delete-icon {
      font-size: 1.1rem;
      font-weight: 700;
      color: $color-white;

      position: absolute;
      top: 49%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &--disabled {
      .attach-file {
        &__container {
          background-color: lighten($color-space-lighten, 5);
        }
        &__button {
          background: rgba($color-space-light, 0) 4.17%;
          color: $color-space;
        }
      }
    }
  }
</style>
